<template>
  <div class="smart-actions-list-wrapper" data-testid="smart-actions-list">
    <!-- List Header -->
    <div v-if="showTitle || showUpgradeLink" class="smart-actions-list-wrapper__header">
      <div v-if="showTitle" class="smart-actions-list-wrapper__title">
        <Icon name="general.stars" size="base" class="smart-actions-list-wrapper__icon" />
        <span>AI Actions</span>
      </div>
      <button
        v-if="showUpgradeLink"
        type="button"
        class="smart-actions-list-wrapper__upgrade-link"
        data-testid="smart-actions-upgrade-link"
        @click="handleUpgrade"
      >
        Upgrade
      </button>
    </div>

    <div v-if="showUsageCounter" class="smart-actions-list-wrapper__limit" data-testid="smart-actions-usage-counter">
      <LimitProgress label="Free monthly AI usage" :current="usageCurrent" :max="usageMax" @upgrade="handleUpgrade" />
    </div>

    <div class="smart-actions-list-wrapper__divider" />

    <!-- List Content -->
    <div class="smart-actions-list-wrapper__content">
      <!-- No Voice Detected State -->
      <div v-if="showNoVoiceDetectedEmptyState" class="smart-actions-list-wrapper__empty" data-testid="smart-actions-empty">
        <Icon name="general.info" size="sm" />
        <span>No voice detected in video</span>
      </div>

      <!-- Smart Actions List -->
      <ul v-else class="smart-actions-list">
        <li v-for="action in visibleActions" :key="action.key">
          <button
            type="button"
            class="smart-action-item"
            :class="{
              'smart-action-item--active': isActiveAction(action.key),
              'smart-action-item--disabled': isActionDisabled,
            }"
            :disabled="isActionDisabled"
            :data-testid="`smart-action-panel-${action.key}`"
            @click="generateSmartAction(action.key)"
          >
            <div class="smart-action-item__icon">
              <Icon :name="getIconForKey(action.key)" size="base" />
            </div>
            <span class="smart-action-item__label">{{ action.title }}</span>
            <span v-if="getActionBadge(action)" class="smart-action-item__badge" :class="getActionBadgeClass(action)">
              {{ getActionBadge(action) }}
            </span>
          </button>
        </li>
      </ul>

      <!-- Mobile: Show more / Show less toggle -->
      <button
        v-if="showCollapseToggle"
        type="button"
        class="smart-actions-list-wrapper__toggle"
        data-testid="smart-actions-toggle"
        @click="isMobileCollapsed = !isMobileCollapsed"
      >
        <span>{{ isMobileCollapsed ? 'Show all actions' : 'Show less' }}</span>
        <Icon :name="isMobileCollapsed ? 'general.chevronDown' : 'general.chevronUp'" size="xs" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon } from '@/storybook';
import { LimitProgress } from '@/storybook/templates/limits';
import { useSmartActionsStore, AiSmartActionStatus } from '@/modules/viewer/store/useSmartActionsStore';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { QuotaFeature } from '@/types/entitlements';
import { useLimits } from '@/composables/useLimits';
import { useMediaQuery } from '@vueuse/core';
import { useSmartActionsNoVoice } from '@/modules/viewer/composables/useSmartActionsNoVoice';

interface Props {
  // Subcategory keys to omit from the list — used to avoid showing "Meeting
  // Summary" here when the panel already has a dedicated Meeting Details
  // section sourced from calendar/attendee data instead.
  excludeKeys?: string[];
  // Hides the "AI Actions" icon/title row without touching the usage
  // counter or upgrade link — for reuse inside a tab that already carries
  // its own label (the tab bar) or its own section heading.
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), { excludeKeys: () => [], showTitle: true });

const smartActionsStore = useSmartActionsStore();
const transcriptionStore = useTranscriptionStore();
const itemStore = useViewerItemStore();
const aiMetadataStore = useAiMetadataStore();
const entitlementsStore = useEntitlementsStore();

const { getSortedCategories, isActioning, primaryCategoryKey, actionType, actionStatus } = storeToRefs(smartActionsStore);
const { isTranscriptionProcessing } = storeToRefs(transcriptionStore);
const { isEditProcessing } = storeToRefs(itemStore);
const { isProcessing: isAiMetadataProcessing } = storeToRefs(aiMetadataStore);
const { hasTerminalNoVoice } = useSmartActionsNoVoice();

const isMobile = useMediaQuery('(max-width: 1023px)');
const isMobileCollapsed = ref(true);

const isProcessing = computed(() => {
  return isAiMetadataProcessing.value || isActioning.value || isEditProcessing.value || isTranscriptionProcessing.value;
});

const isActiveAction = (key: string): boolean => {
  return actionType.value === key && actionStatus.value === AiSmartActionStatus.DONE;
};

const { openLimitsModal, isPricingUpdateEnabled } = useLimits();

const showUsageCounter = computed(() => {
  if (isPricingUpdateEnabled.value) {
    return false;
  }
  const limit = entitlementsStore.getLimit(QuotaFeature.SMART_ACTIONS.limitKey);
  return limit !== null && limit !== undefined && limit > 0;
});

const usageMax = computed(() => entitlementsStore.getLimit(QuotaFeature.SMART_ACTIONS.limitKey) ?? 0);
const usageCurrent = computed(() => Math.min(entitlementsStore.getQuota(QuotaFeature.SMART_ACTIONS.quotaKey)?.used ?? 0, usageMax.value));

const isLocked = computed(() => entitlementsStore.isFeatureLocked(QuotaFeature.SMART_ACTIONS));
const isActionDisabled = computed(() => isProcessing.value || isLocked.value);
const showUpgradeLink = computed(() => isLocked.value && !showUsageCounter.value && !isPricingUpdateEnabled.value);

const showNoVoiceDetectedEmptyState = hasTerminalNoVoice;

const handleUpgrade = () => {
  openLimitsModal();
};

const icons: Record<string, string> = {
  meeting_summary: 'ai.meetingSummary',
  action_items: 'ai.actionItems',
  step_by_step_guide: 'ai.stepByStepGuide',
  technical_documentation: 'ai.technicalDocumentation',
  blog_post: 'ai.blogPost',
  bug_report: 'ai.bugReport',
  feature_request: 'ai.keyTakeaways',
  sales_pitch: 'ai.salesPitch',
  email: 'ai.email',
};

const getIconForKey = (key: string): string => {
  return icons[key] || 'general.stars';
};

const isSuggestedCategory = (category: string): boolean => {
  return (primaryCategoryKey?.value ?? '') === category;
};

type SmartActionListItem = { key: string; title: string; isSuggested: boolean };

const getActionBadge = (action: SmartActionListItem): string | null => {
  if (isActiveAction(action.key)) {
    return action.isSuggested ? 'Auto-applied' : 'Applied';
  }

  return action.isSuggested ? 'Suggested' : null;
};

const getActionBadgeClass = (action: SmartActionListItem): string => {
  return isActiveAction(action.key) ? 'smart-action-item__badge--applied' : 'smart-action-item__badge--suggested';
};

const flatActions = computed(() => {
  const actions: SmartActionListItem[] = [];
  const categories = getSortedCategories.value || [];

  for (const category of categories) {
    const isSuggested = isSuggestedCategory(category.key);
    for (const action of category.subcategories || []) {
      if (props.excludeKeys.includes(action.key)) continue;
      actions.push({
        key: action.key,
        title: action.title,
        isSuggested,
      });
    }
  }

  return actions.sort((a, b) => Number(isActiveAction(b.key)) - Number(isActiveAction(a.key)));
});

// On mobile when collapsed: show active action, or suggested, or first action
const visibleActions = computed(() => {
  if (!isMobile.value || !isMobileCollapsed.value) {
    return flatActions.value;
  }

  // Find the active action first
  const active = flatActions.value.find(a => isActiveAction(a.key));
  if (active) {
    return [active];
  }

  // Fall back to suggested action
  const suggested = flatActions.value.find(a => a.isSuggested);
  if (suggested) {
    return [suggested];
  }

  // Fall back to first action
  if (flatActions.value.length > 0) {
    return [flatActions.value[0]];
  }

  return [];
});

// Only show toggle on mobile when there are more actions than visible
const showCollapseToggle = computed(() => {
  return isMobile.value && !showNoVoiceDetectedEmptyState.value && flatActions.value.length > 1;
});

const generateSmartAction = (key: string) => {
  smartActionsStore.requestSmartAction(key);
};
</script>

<style scoped>
.smart-actions-list-wrapper__header {
  @apply flex items-center justify-between px-4 sm:px-5 pt-4 pb-3;
}

.smart-actions-list-wrapper__title {
  @apply flex items-center gap-2 text-base sm:text-lg font-semibold text-zinc-900 dark:text-zinc-100;
}

.smart-actions-list-wrapper__icon {
  color: #9557af;
}

.dark .smart-actions-list-wrapper__icon {
  color: #e0adff;
}

.smart-actions-list-wrapper__upgrade-link {
  @apply text-xs font-semibold text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 transition-colors cursor-pointer;
}

.smart-actions-list-wrapper__limit {
  @apply px-4 sm:px-5 pb-3;
}

.smart-actions-list-wrapper__divider {
  @apply mx-4;
  height: 2px;
  background: linear-gradient(90deg, #f5d9b7 0%, #e0adff 50%, #f5d9b7 100%);
  border-radius: 1px;
}

.dark .smart-actions-list-wrapper__divider {
  background: linear-gradient(90deg, rgba(245, 217, 183, 0.4) 0%, rgba(224, 173, 255, 0.5) 50%, rgba(245, 217, 183, 0.4) 100%);
}

.smart-actions-list-wrapper__content {
  @apply flex flex-col px-2 py-2;
}

.smart-actions-list-wrapper__empty {
  @apply flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 px-2 py-3;
}

.smart-actions-list-wrapper__toggle {
  @apply flex items-center justify-center gap-1.5 w-full py-2 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors cursor-pointer;
}

.smart-actions-list {
  @apply flex flex-col;
  list-style: none;
  margin: 0;
  padding: 0;
}

.smart-action-item {
  @apply flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors cursor-pointer;
}

.smart-action-item:hover {
  @apply bg-zinc-50 dark:bg-zinc-700/50;
}

.smart-action-item--active {
  background: linear-gradient(90deg, rgba(224, 173, 255, 0.15) 0%, rgba(224, 173, 255, 0.3) 50%, rgba(224, 173, 255, 0.05) 100%);
}

.dark .smart-action-item--active {
  background: linear-gradient(90deg, rgba(224, 173, 255, 0.1) 0%, rgba(224, 173, 255, 0.2) 50%, rgba(224, 173, 255, 0.05) 100%);
}

.smart-action-item--active:hover {
  background: linear-gradient(90deg, rgba(224, 173, 255, 0.2) 0%, rgba(224, 173, 255, 0.35) 50%, rgba(224, 173, 255, 0.1) 100%);
}

.dark .smart-action-item--active:hover {
  background: linear-gradient(90deg, rgba(224, 173, 255, 0.15) 0%, rgba(224, 173, 255, 0.25) 50%, rgba(224, 173, 255, 0.1) 100%);
}

.smart-action-item--disabled {
  @apply opacity-50 cursor-not-allowed;
}

.smart-action-item__icon {
  @apply w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%), linear-gradient(90deg, #f5d9b7 0%, #e0adff 100%);
  color: #9557af;
}

.dark .smart-action-item__icon {
  background: linear-gradient(90deg, rgba(245, 217, 183, 0.3) 0%, rgba(224, 173, 255, 0.3) 100%);
  color: #e0adff;
}

.smart-action-item__label {
  @apply text-sm font-medium text-zinc-700 dark:text-zinc-200 flex-grow;
}

.smart-action-item__badge {
  @apply text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0;
}

.smart-action-item__badge--applied {
  background-image: linear-gradient(to right, #9557af, #483473);
  @apply text-white;
}

.smart-action-item__badge--suggested {
  @apply bg-orange-100 text-orange-800 dark:bg-orange-800/40 dark:text-orange-300;
}
</style>
